import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Tag,
  Typography,
  Space,
  Select,
  Input,
  Badge,
  Tooltip,
  message,
  Button,
  Row,
  Col,
  Card,
} from "antd";
import {
  SettingOutlined,
  ProfileOutlined,
  RiseOutlined,
  SlidersOutlined,
  SearchOutlined,
  ReloadOutlined,
  WalletOutlined,
  BankOutlined,
  CustomerServiceOutlined,
  UserOutlined,
} from "@ant-design/icons";
import axios from "axios";
import consts from "../../constant";
import Loadingpage from "../Loadingpage/Loadingpage";

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const TYPE_META = {
  settings: { label: "Settings", color: "blue", icon: <SettingOutlined /> },
  pairs: { label: "Pairs", color: "purple", icon: <ProfileOutlined /> },
  deposit: { label: "Deposit", color: "cyan", icon: <WalletOutlined /> },
  withdraw: { label: "Withdraw", color: "magenta", icon: <BankOutlined /> },
  support: { label: "Support", color: "geekblue", icon: <CustomerServiceOutlined /> },
  user: { label: "User", color: "volcano", icon: <UserOutlined /> },
  tradesettings: { label: "Trade Settings", color: "orange", icon: <SlidersOutlined /> },
};

const normalizeType = (t) => (t || "").replace(/\s+/g, "").toLowerCase();

const SubadminActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [typeCounts, setTypeCounts] = useState({});
  const [filterType, setFilterType] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchText]);

  const fetchActivities = useCallback(
    async (page = 1, limit = 10) => {
      setLoading(true);
      try {
        const params = { page, limit };
        if (filterType && filterType !== "all") params.type = filterType;
        if (debouncedSearchText) params.search = debouncedSearchText;
        const response = await axios.get(
          `${consts.BackendUrl}/notification/getSubadminActivities`,
          {
            params,
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Jetix")}`,
            },
          }
        );
        const data = response?.data;
        if (data?.success && data?.result?.docs) {
          setActivities(data.result.docs);
          setTypeCounts(data.counts || {});
          setPagination((prev) => ({
            ...prev,
            current: data.result.page,
            total: data.result.totalDocs,
          }));
        } else {
          setActivities([]);
          setTypeCounts({});
        }
      } catch (err) {
        message.error("Failed to fetch subadmin activities");
        setActivities([]);
        setTypeCounts({});
      } finally {
        setLoading(false);
      }
    },
    [filterType, debouncedSearchText]
  );

  useEffect(() => {
    fetchActivities(1, pagination.pageSize);
  }, [filterType, debouncedSearchText]);

  const handleTableChange = (pag) => {
    setPagination((prev) => ({ ...prev, current: pag.current, pageSize: pag.pageSize }));
    fetchActivities(pag.current, pag.pageSize);
  };

  const columns = [
    {
      title: "SubAdmin",
      dataIndex: "from",
      key: "from",
      render: (from) => {
        const name = from?.name || "Unknown";
        const role = from?.role || "subadmin";
        const initials = name
          .split(" ")
          .map((w) => w[0])
          .join("")
          .toUpperCase()
          .slice(0, 2);
        return (
          <Space>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "transparent",
                border: "1px solid var(--primary-color)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--primary-color)",
                fontWeight: 700,
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              {initials}
            </div>
            <div>
              <Text strong style={{ color: "#fff", display: "block", fontSize: 13 }}>
                {name}
              </Text>
              <Text style={{ color: "var(--primary-color)", fontSize: 11, opacity: 0.8 }}>
                {from?.email || role}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: "Activity Type",
      dataIndex: "type",
      key: "type",
      width: 160,
      render: (type) => {
        const norm = normalizeType(type);
        const meta = TYPE_META[norm] || { label: type, color: "default", icon: null };
        return (
          <Tag
            icon={meta.icon}
            color={meta.color}
            style={{ fontWeight: 600, borderRadius: 8, padding: "2px 10px" }}
          >
            {meta.label}
          </Tag>
        );
      },
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      render: (msg) => (
        <Tooltip title={msg}>
          <Text
            style={{
              color: "#e0ffe8",
              fontSize: 13,
              display: "block",
              maxWidth: 380,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {msg}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Date & Time",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (date) => (
        <Text style={{ color: "var(--primary-color)", fontSize: 12 }}>
          {new Date(date).toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    },
  ];

  return (
    <>
      <Loadingpage />
      <div style={{ padding: "0 0 32px 0" }}>
        {/* Header */}
        <div className="display-2 responsive-group p-10">
          <h2>SubAdmin Activities</h2>
          <div className="display-1 responsive-inputgroup">
            <Search
              placeholder="Search by name or message…"
              onSearch={(value) => {
                setPagination((prev) => ({ ...prev, current: 1 }));
                setSearchText(value);
                setDebouncedSearchText(value);
              }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              loading={loading}
              className="input-cls"
              style={{ width: 260 }}
              allowClear
            />
            <div className="display-1">
              <Select
                value={filterType === "all" ? undefined : filterType}
                onChange={(v) => {
                  setFilterType(v || "all");
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                style={{ width: 150 }}
                placeholder="Select Type"
                allowClear
              >
                <Option value="all">All Types</Option>
                {Object.entries(TYPE_META).map(([key, meta]) => (
                  <Option key={key} value={key}>
                    {meta.label}
                  </Option>
                ))}
              </Select>
              <Button
                type="primary"
                onClick={() => {
                  setSearchText("");
                  setDebouncedSearchText("");
                  setFilterType("all");
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
              >
                Clear
              </Button>
            </div>
          </div>
        </div>

        {/* Stats cards - Using project standard Row/Col/Card */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24, padding: "0 10px" }}>
          {Object.entries(TYPE_META).map(([key, meta]) => (
            <Col xs={24} sm={12} md={6} key={key}>
              <Card
                className="card-bg-clr"
                hoverable
                onClick={() => setFilterType(filterType === key ? "all" : key)}
                style={{
                  cursor: "pointer",
                  border: filterType === key ? "2px solid var(--primary-color)" : "",
                  boxShadow: filterType === key ? "0 0 15px rgba(46, 255, 12, 0.3)" : "",
                  transition: "all 0.3s ease",
                  borderRadius: 12,
                }}
                bodyStyle={{ padding: "16px 20px" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span style={{ color: "var(--primary-color)", fontSize: 20 }}>
                    {meta.icon}
                  </span>
                  <Text style={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>
                    {meta.label}
                  </Text>
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: "var(--primary-color)" }}>
                  {typeCounts[key] || 0}
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Table - Using project standard table-clr */}
        <div className="p-10">
          <Table
            dataSource={activities}
            columns={columns}
            rowKey="_id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: false, // hidden as per request
              showTotal: (total) => (
                <span style={{ color: "var(--primary-color)" }}>Total {total} activities</span>
              ),
              style: { padding: "12px 16px" },
              className: "pagination-clr",
            }}
            onChange={handleTableChange}
            scroll={{ x: 700 }}
            className="table-clr"
            locale={{
              emptyText: (
                <div style={{ padding: "40px 0", textAlign: "center", color: "var(--primary-color)", opacity: 0.5 }}>
                  No activities found
                </div>
              ),
            }}
          />
        </div>

        {/* Minimal inline style overrides for unique activity log features */}
        <style>{`
        .ant-table-thead > tr > th {
          text-transform: uppercase;
          font-size: 11px !important;
          letter-spacing: 1px;
        }
        .ant-table-tbody > tr > td {
          font-size: 13px !important;
        }
        .ant-table-tbody > tr:hover > td {
          background: rgba(46, 255, 12, 0.05) !important;
        }
      `}</style>
      </div>
    </>
  );
};

export default SubadminActivities;
